import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import SearchSelect from '../../../components/SearchSelect';
import { withCurrent } from '../../../utils/searchOptions';
import {
    SchemaFieldKeySelect,
    useSchemaFieldDict,
} from '../../../components/SchemaFieldKeySelect';
import { FieldIssue } from '../../../components/wizard/WizardShell';
import {
    BundleDeviceParamItem,
    DeviceBundlePayload,
    DeviceBundlePreflight,
} from '../../../api/bundle';
import { listAllDevices } from '../../../api/device';
import { listDict } from '../../../api/dict';
import { loadMaterialOptions } from '../../../api/material';
import {
    categoryPairFallbackLabel,
    decodeCategoryPair,
    encodeCategoryPair,
    useCategoryPairOptions,
} from '../../../utils/categoryOptions';
import {
    listMMGroups,
    loadMModesCached,
    MMGroup,
    MMMode,
    mmModeLabel,
} from '../../../api/mmode';
import {
    bestSchema,
    deleteSchema,
    listParams,
    listProcessArchs,
    listSchemaBindings,
    MaterialKeyKind,
    ParamRow,
    resolveSchema,
    RESULT_TAGS,
    upsertSchema,
} from '../../../api/parameter';
import { alertApprovalIfPending, ApiError } from '../../../api/request';
import { useCan } from '../../../store';
import { alertDialog, confirmDialog } from '../../../utils/dialog';
import { DictOptions, useDictOptions } from '../../../utils/dictOptions';
import { useSchemaFieldNames } from '../../../utils/useSchemaFieldNames';
import { fieldDisplayName } from '../../../api/schemaField';
import SchemaEditModal, {
    FieldSpec,
    parseFields,
} from '../../parameter/SchemaEditModal';
import { groupAppliesToArch } from '../../../components/mmode/archUtils';
import { IssueByPath } from './useDeviceBundleDraft';
import { withPendingGroups, withPendingModes } from './newDicts';
import {
    Layout,
    parseLayout,
    stringifyLayout,
} from '../../../utils/fieldGroups';
import GroupedFields from '../../../components/GroupedFields';

const specAttrs = (f: FieldSpec) => f;
const specKey = (f: FieldSpec) => f.k;

// 设备向导第 ⑤ 步: 加工参数行 (可选), 随套件一个工单一个事务落库, 通过即 published.
//
// 与材料向导第 ④ 步"只能挑已有参数"不同, 建设备时参数就是要**新录**的 (新机器的参数别处没有),
// 所以这里是可编辑表单。字段集口径与「工艺参数」页完全一致:
//   1. 关键字段 (材料键 / 加工类型 / 加工模式) 决定命中哪张参数模板 —— 按框架 materialKeyKind 三选一;
//   2. 参数值区**按解析到的模板字段渲染** (resolveSchema, 特异度 设备 > 存量机型模板 > 模式组 > 加工类型),
//      模板未声明但行里已有值的字段追加在末尾 (复制来的行 / 恢复的草稿), 不静默丢弃;
//   3. 字段 key 白名单 (schema_field 字典)、必填 / 数值 / 区间校验都在服务端 dryRun, 前端不复刻。
// 参数模板按设备建 (2026-11-01): 新设备没有自己的模板, 在「参数模板」卡从相近设备的模板复制成本设备的
// (键 = 本 deviceKey + 模式组 + 加工类型); 存量按机型建的模板在拆分完之前仍按机型 (family) 命中。
//
// 从已有设备复制: 新机型的参数多半照着同框架的老机型抄再调 (6 头机 4 个振镜头尤其), 所以给一个
// 按设备 / 关键词搜已发布行的面板, 选中即复制成本设备的行 (材料键 / 加工类型 / 值 / 结果标照搬)。

interface Props {
    payload: DeviceBundlePayload;
    setPayload: (p: DeviceBundlePayload) => void;
    preflight: DeviceBundlePreflight | null;
    issueByPath: IssueByPath;
    /** 就地复制了参数模板后让向导重取 preflight (新模板要立刻能被解析命中) */
    onSchemaChanged: () => void;
}

/** TplOption 可搜索的参数模板候选 (来自 preflight.paramSchemas). */
interface TplOption {
    key: string;
    label: string;
    /** 模板所属设备; 空 = 存量按机型建的模板 */
    deviceKey: string;
    family: string;
    /** 存量模板的设备绑定清单 (命中打分用) */
    deviceKeys?: string[];
    groupCode: string;
    processType: string;
    fields: FieldSpec[];
    /** 分组布局 (2026-10-23); 复制/编辑模板时随字段一起带走 */
    layout: Layout;
}

/** tplKeyOf 模板键 → 唯一串 (deviceKey|family|group|processType). */
function tplKeyOf(t: {
    deviceKey?: string;
    family?: string;
    groupCode?: string;
    processType?: string;
}): string {
    return `${t.deviceKey || ''}|${t.family || ''}|${t.groupCode || ''}|${t.processType || ''}`;
}

/** TplKeyParts 模板键各段 (人读标签 / 命中说明用). */
type TplKeyParts = {
    deviceKey?: string;
    family?: string;
    groupCode?: string;
    processType?: string;
};

/** COPY_MAX 从源设备复制时最多加载的行数. */
const COPY_MAX = 1000;

const RESULT_TAG_OPTS = RESULT_TAGS.map((t) => ({
    value: t.v,
    label: t.label,
}));
const BOOL_OPTS = [
    { value: 'true', label: '是' },
    { value: 'false', label: '否' },
];

/** KEY_FIELD_KEYS 在关键字段区单独渲染的动态字段 (与参数管理页同一集合). */
const KEY_FIELD_KEYS = new Set(['processingMode']);

const ARCH_KEY_LABEL: Record<MaterialKeyKind, string> = {
    xcode: '材料 (xcode)',
    dual_xcode: '膜材 / 基材 (xcode)',
    category_code: '材料分类 (一级 / 二级)',
};

function parseParams(raw?: string): Record<string, unknown> {
    if (!raw) return {};
    try {
        const v = JSON.parse(raw);
        return v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
    } catch {
        return {};
    }
}

function StepDeviceParams({
    payload,
    setPayload,
    preflight,
    issueByPath,
    onSchemaChanged,
}: Props) {
    const { archCode, deviceKey } = payload.device;
    // 机型只用于命中存量按机型建的模板 (过渡期); 新设备落库前后端查不到它的机型, 这里显式带上
    const family = payload.device.family || deviceKey;
    const rows = useMemo(() => payload.params || [], [payload.params]);
    const setRows = useCallback(
        (next: BundleDeviceParamItem[]) =>
            setPayload({ ...payload, params: next }),
        [payload, setPayload],
    );

    const [keyKind, setKeyKind] = useState<MaterialKeyKind>('xcode');
    const [processTypes, setProcessTypes] = useState<
        { code: string; label: string }[]
    >([]);
    const [dictModes, setMModes] = useState<MMMode[]>([]);
    const [dictGroups, setMMGroups] = useState<MMGroup[]>([]);
    // 字典 + 第 ② 步待随套件新建的模式值/模式组 (newDicts), 参数行一样能选
    const mmodes = useMemo(
        () => withPendingModes(dictModes, payload.newDicts),
        [dictModes, payload.newDicts],
    );
    const mmGroups = useMemo(
        () => withPendingGroups(dictGroups, payload.newDicts),
        [dictGroups, payload.newDicts],
    );
    // 机型 → 设备名 (模板候选里 GS004-CLASS-4 这种码没人记得住, 旁边带上 F2 Ultra)
    const [familyNames, setFamilyNames] = useState<Record<string, string>>({});
    const fieldNames = useSchemaFieldNames();

    useEffect(() => {
        if (!archCode) return;
        listProcessArchs()
            .then((list) => {
                const a = list.find((x) => x.code === archCode);
                if (a) setKeyKind(a.materialKeyKind || 'xcode');
            })
            .catch(() => undefined);
        listDict('process_type', { scope: archCode })
            .then((es) =>
                setProcessTypes(
                    es.map((e) => ({ code: e.code, label: e.label })),
                ),
            )
            .catch(() => setProcessTypes([]));
        loadMModesCached()
            .then(setMModes)
            .catch(() => setMModes([]));
        listMMGroups()
            .then(setMMGroups)
            .catch(() => setMMGroups([]));
        listAllDevices({ archCode })
            .then((list) => {
                const m: Record<string, string> = {};
                list.forEach((d) => {
                    if (d.family && d.name && !m[d.family])
                        m[d.family] = d.name;
                });
                setFamilyNames(m);
            })
            .catch(() => setFamilyNames({}));
    }, [archCode]);

    // 模板键 → 人读标签: 设备 (存量模板为机型名) / 模式组中文 / 加工类型中文 (码保留在后面, 仍可按码搜)
    const tplLabelOf = useCallback(
        (t: TplKeyParts) => {
            const parts: string[] = [];
            if (t.deviceKey) {
                parts.push(t.deviceKey);
            } else if (t.family) {
                parts.push(
                    familyNames[t.family]
                        ? `${familyNames[t.family]} ${t.family}`
                        : t.family,
                );
            }
            if (t.groupCode) {
                const g = mmGroups.find((x) => x.code === t.groupCode);
                parts.push(
                    g?.nameZh ? `${g.nameZh} ${t.groupCode}` : t.groupCode,
                );
            }
            if (t.processType) {
                const pt = processTypes.find((x) => x.code === t.processType);
                parts.push(
                    pt?.label ? `${pt.label} ${t.processType}` : t.processType,
                );
            }
            return parts.length ? parts.join(' / ') : '框架默认';
        },
        [familyNames, mmGroups, processTypes],
    );

    /**
     * 兜底洗掉与本框架粒度不符的材料键 (2026-09-22): 草稿是跨框架恢复的、或换框架前就填了行时,
     * 行上会留着上一个框架的 xcode/filmXcode —— 提交必报"材料键与框架粒度不符", 而表单里
     * 只渲染本框架那一种键, 人看不到那个残留值也就无从删起.
     */
    useEffect(() => {
        const keep: Record<MaterialKeyKind, (keyof BundleDeviceParamItem)[]> = {
            xcode: ['xcode'],
            dual_xcode: ['filmXcode', 'substrateXcode'],
            category_code: ['type2', 'type1'],
        };
        const allKeys: (keyof BundleDeviceParamItem)[] = [
            'xcode',
            'filmXcode',
            'substrateXcode',
            'type2',
            'type1',
        ];
        const drop = allKeys.filter((k) => !keep[keyKind].includes(k));
        let changed = false;
        const next = rows.map((r) => {
            if (!drop.some((k) => r[k])) return r;
            changed = true;
            const c = { ...r };
            drop.forEach((k) => delete c[k]);
            return c;
        });
        if (changed) setRows(next);
        // 只在框架粒度变化时洗一次; rows 的日常编辑不触发
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keyKind]);

    // 只有第 ② 步勾了的模式才能选 (服务端: 引用未声明的模式只给 warning, 但那多半是漏勾)
    const modeOptions = useMemo(() => {
        const declared = new Set(
            (payload.modeRefs || []).map((r) => r.modeValue),
        );
        const list = mmodes.filter((m) => declared.has(m.value));
        return list.length ? list : mmodes;
    }, [mmodes, payload.modeRefs]);
    const groupOf = useCallback(
        (modeValue: string) =>
            mmodes.find((m) => m.value === modeValue)?.groupCode || '',
        [mmodes],
    );

    // ── 模板解析: 按 (模式组, 加工类型) 缓存, 同签名的行共用一份字段集 ──
    /** ResolvedTpl 一次模板解析的结果: 字段集 + 实际命中的模板键 (让人看见"为什么是这几个字段"). */
    interface ResolvedTpl {
        fields: FieldSpec[];
        /** 命中模板的 页签 → 分组 (模板没分组 = null, 表单平铺) */
        layout: Layout;
        /** 命中模板的键: 空段 = 通配; 全空 = 框架默认模板 */
        hit: Required<TplKeyParts> | null;
    }
    const [tplCache, setTplCache] = useState<Record<string, ResolvedTpl>>({});
    const inflight = useRef(new Set<string>());
    const sigOf = useCallback(
        (r: BundleDeviceParamItem) =>
            `${groupOf(String(r.params?.processingMode || ''))}|${r.subProcess || ''}`,
        [groupOf],
    );
    useEffect(() => {
        if (!archCode) return;
        rows.forEach((r) => {
            const sig = sigOf(r);
            if (tplCache[sig] || inflight.current.has(sig)) return;
            inflight.current.add(sig);
            const [groupCode, processType] = sig.split('|');
            resolveSchema(
                archCode,
                { family, groupCode, processType },
                deviceKey,
            )
                .then((tpl) =>
                    setTplCache((cur) => ({
                        ...cur,
                        [sig]: {
                            fields: parseFields(tpl.fields),
                            layout: parseLayout(tpl.layout),
                            hit: {
                                deviceKey: tpl.deviceKey,
                                family: tpl.family,
                                groupCode: tpl.groupCode,
                                processType: tpl.processType,
                            },
                        },
                    })),
                )
                .catch(() =>
                    setTplCache((cur) => ({
                        ...cur,
                        [sig]: { fields: [], layout: null, hit: null },
                    })),
                )
                .finally(() => inflight.current.delete(sig));
        });
    }, [rows, archCode, family, deviceKey, sigOf, tplCache]);

    // 模板字段的默认值 (f.v) 补进空字段 (已填的一律不覆盖), 与参数页 applyTemplateDefaults 同口径
    useEffect(() => {
        let changed = false;
        const next = rows.map((r) => {
            const tpl = tplCache[sigOf(r)];
            if (!tpl) return r;
            let rowChanged = false;
            const params = { ...(r.params || {}) };
            tpl.fields.forEach((f) => {
                if (f.v === undefined || KEY_FIELD_KEYS.has(f.k)) return;
                if (params[f.k] === undefined || params[f.k] === '') {
                    params[f.k] = f.v;
                    rowChanged = true;
                }
            });
            if (!rowChanged) return r;
            changed = true;
            return { ...r, params };
        });
        if (changed) setRows(next);
        // 只在模板缓存变化时补默认值; rows 变化由用户编辑驱动, 不重复触发
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tplCache]);

    // 本框架全部参数模板 (可搜索候选): 行级"字段来源"覆盖与"复制为本设备模板"都从这里挑
    const tplOptions = useMemo<TplOption[]>(
        () =>
            (preflight?.paramSchemas || [])
                .map((t) => {
                    const fields = parseFields(t.fields);
                    return {
                        key: tplKeyOf(t),
                        label: `${tplLabelOf(t)} · ${fields.length} 字段`,
                        deviceKey: t.deviceKey || '',
                        family: t.family,
                        deviceKeys: t.deviceKeys,
                        groupCode: t.groupCode,
                        processType: t.processType,
                        fields,
                        layout: parseLayout(t.layout),
                    };
                })
                .filter((t) => t.fields.length > 0),
        [preflight, tplLabelOf],
    );
    const tplByKey = useMemo(
        () => new Map(tplOptions.map((t) => [t.key, t])),
        [tplOptions],
    );
    // 复制模板落库 (或送审) 后: 模板缓存作废重解析, 并让向导重取 preflight
    const onTemplatesChanged = useCallback(() => {
        setTplCache({});
        onSchemaChanged();
    }, [onSchemaChanged]);

    // 全部模板用到的字典来源 (f.os), 一次拉齐 (含行级覆盖用到的模板)
    const allSpecs = useMemo(
        () => [
            ...Object.values(tplCache).flatMap((t) => t.fields),
            ...tplOptions.flatMap((t) => t.fields),
        ],
        [tplCache, tplOptions],
    );
    const dictOpts = useDictOptions(
        allSpecs.map((f) => f.os),
        archCode,
    );

    const addRow = () =>
        setRows([
            ...rows,
            {
                params: { processingMode: modeOptions[0]?.value || '' },
                resultTag: 'recommended',
            },
        ]);
    const dupRow = (i: number) =>
        setRows([
            ...rows.slice(0, i + 1),
            JSON.parse(JSON.stringify(rows[i])),
            ...rows.slice(i + 1),
        ]);
    const patchRow = (i: number, p: Partial<BundleDeviceParamItem>) =>
        setRows(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
    const patchParam = (i: number, k: string, v: unknown) => {
        const r = rows[i];
        const params = { ...(r.params || {}) };
        if (v === '' || v === undefined || v === null) delete params[k];
        else params[k] = v;
        patchRow(i, { params });
    };

    if (!archCode) {
        return (
            <div className="card p-4 text-sm text-slate-500">
                请先在第 1 步选择工艺框架,
                参数行的材料键粒度与加工类型都按框架来。
            </div>
        );
    }

    return (
        <>
            <div className="infobox text-xs">
                参数行随本套件一个工单审批,
                通过即发布。字段集按命中的参数模板渲染
                (与「工艺参数」页同一口径), 模板里没有的字段可以手动补 (须在
                Schema 字段字典内)。本步可整步跳过, 创建后去参数页补录。
            </div>

            <TemplateCopyCard
                archCode={archCode}
                deviceKey={deviceKey}
                family={family}
                options={tplOptions}
                mmGroups={mmGroups.filter((g) =>
                    groupAppliesToArch(g.archCodes, archCode),
                )}
                processTypes={processTypes}
                onChanged={onTemplatesChanged}
            />

            <CopyFromDevicePanel
                archCode={archCode}
                excludeDeviceKey={deviceKey}
                templateKeyFor={(srcDevice, srcFamily, mode, sub) =>
                    bestSchema(tplOptions, srcDevice, srcFamily, groupOf(mode), sub)?.key
                }
                keyKind={keyKind}
                processTypes={processTypes}
                onPick={(items) => setRows([...rows, ...items])}
            />

            <div className="card p-4 space-y-3">
                <div className="text-sm font-semibold flex items-center gap-2">
                    本设备的参数行 ({rows.length})
                    <span className="text-xs font-normal text-slate-400">
                        材料键: {ARCH_KEY_LABEL[keyKind]}
                    </span>
                    <button
                        type="button"
                        className="ml-auto text-xs text-brand-600 hover:underline font-normal"
                        onClick={addRow}
                    >
                        ＋ 添加一行
                    </button>
                </div>
                {rows.length === 0 && (
                    <div className="text-xs text-slate-400">
                        还没有参数行。可以手动添加, 或从上方复制已有设备的参数。
                    </div>
                )}
                <FieldIssue issue={issueByPath['params']} />
            </div>

            {rows.map((r, i) => (
                <ParamRowCard
                    key={i}
                    index={i}
                    row={r}
                    keyKind={keyKind}
                    processTypes={processTypes}
                    modeOptions={modeOptions}
                    tpl={tplCache[sigOf(r)]}
                    tplOptions={tplOptions}
                    tplByKey={tplByKey}
                    tplLabelOf={tplLabelOf}
                    deviceKey={deviceKey}
                    dictOpts={dictOpts}
                    fieldNames={fieldNames}
                    issueByPath={issueByPath}
                    onPatch={(p) => patchRow(i, p)}
                    onParam={(k, v) => patchParam(i, k, v)}
                    onDup={() => dupRow(i)}
                    onRemove={() => setRows(rows.filter((_, j) => j !== i))}
                />
            ))}
        </>
    );
}

/**
 * TemplateCopyCard 参数模板 (2026-09-22 按用户预期改; 2026-11-01 模板改按设备建): 新设备没有自己的模板, 流程是
 *   ① 搜索一张已有模板 (任意设备) → ② 复制并编辑字段 (复用参数模板管理页的字段编辑弹窗,
 *   可增删字段 / 改类型 / 区间 / 默认值) → ③ 保存为本设备的模板 (键 = 本 deviceKey + 模式组 + 加工类型;
 *   走参数模板写接口: admin 直落, 非 admin 送审) → ④ 下面的参数行选这张模板填数值。
 * 本设备已有的模板列在最上面, 可继续改字段。
 */
function TemplateCopyCard({
    archCode,
    deviceKey,
    family,
    options,
    mmGroups,
    processTypes,
    onChanged,
}: {
    archCode: string;
    deviceKey: string;
    /** 向导里填的机型: 设备还没落库, 平台查不到它的机型, 建模板时带上 (落库后平台按设备再校正) */
    family: string;
    options: TplOption[];
    mmGroups: MMGroup[];
    processTypes: { code: string; label: string }[];
    onChanged: () => void;
}) {
    const canCreate = useCan('param_schema', 'create');
    const canDelete = useCan('param_schema', 'delete');
    const [picked, setPicked] = useState('');
    // 字段编辑弹窗: 复制源模板 / 编辑本设备已有模板 共用
    const [editor, setEditor] = useState<{
        fields: FieldSpec[];
        layout: Layout;
        groupCode: string;
        processType: string;
        title: string;
    } | null>(null);
    const [busy, setBusy] = useState('');
    const [msg, setMsg] = useState('');

    const mine = useMemo(
        () => options.filter((t) => !!deviceKey && t.deviceKey === deviceKey),
        [options, deviceKey],
    );
    const others = useMemo(
        () => options.filter((t) => !deviceKey || t.deviceKey !== deviceKey),
        [options, deviceKey],
    );
    const selectOptions = useMemo(
        () => others.map((t) => ({ value: t.key, label: t.label })),
        [others],
    );
    const groupLabel = (code: string) =>
        code
            ? mmGroups.find((g) => g.code === code)?.nameZh || code
            : '任意模式组';
    const processLabel = (code: string) =>
        code
            ? processTypes.find((p) => p.code === code)?.label || code
            : '任意加工类型';
    const pickedTpl = picked
        ? options.find((t) => t.key === picked)
        : undefined;

    const requireDevice = () => {
        if (deviceKey) return true;
        alertDialog('先在第 ① 步填 device_key, 才知道模板建给哪台设备。');
        return false;
    };

    // 复制 = 原样带走源模板的键 (模式组 / 加工类型), 设备换成本设备; 字段在弹窗里改
    const openCopyEditor = () => {
        const t = options.find((x) => x.key === picked);
        if (!t || !requireDevice()) return;
        setEditor({
            fields: t.fields,
            layout: t.layout,
            groupCode: t.groupCode,
            processType: t.processType,
            title: `复制自 ${t.label}`,
        });
    };

    /** save 保存为本设备模板 (同键已有则覆盖 —— 这就是"编辑本设备模板"). */
    const save = async (
        fields: FieldSpec[],
        layout: Layout,
        groupCode: string,
        processType: string,
    ) => {
        const res = await upsertSchema(
            archCode,
            JSON.stringify(fields),
            { deviceKey, family, groupCode, processType },
            stringifyLayout(layout),
        );
        const pending =
            !!res.data?.taskCode || alertApprovalIfPending(res.message);
        setMsg(
            pending
                ? `模板 ${deviceKey} / ${groupLabel(groupCode)} / ${processLabel(processType)} 已送审, 通过后生效`
                : `模板 ${deviceKey} / ${groupLabel(groupCode)} / ${processLabel(processType)} 已保存 (${fields.length} 字段)`,
        );
        if (!pending) onChanged();
    };

    /**
     * remove 删本设备的一张模板. 与参数模板管理页同一套保护: 先查有没有参数行正命中它,
     * 有就挡住 (那些行的字段集会塌回通配模板), 没有才确认删除.
     */
    const remove = async (t: TplOption) => {
        setBusy(`del:${t.key}`);
        setMsg('');
        try {
            const r = await listSchemaBindings(archCode, t);
            if (r.total > 0) {
                alertDialog(
                    `模板「${groupLabel(t.groupCode)} / ${processLabel(t.processType)}」不能删除: ` +
                        `已有 ${r.total} 条参数行命中它。请先改这些行的键或删掉它们。`,
                );
                return;
            }
            const ok = await confirmDialog(
                `确认删除模板「${groupLabel(t.groupCode)} / ${processLabel(t.processType)}」(${t.fields.length} 字段)? ` +
                    '当前没有参数行命中它。',
            );
            if (!ok) return;
            const res = await deleteSchema(archCode, t);
            const pending = !!res.data && alertApprovalIfPending(res.message);
            setMsg(
                pending
                    ? `模板 ${groupLabel(t.groupCode)} / ${processLabel(t.processType)} 的删除已送审, 通过后生效`
                    : `模板 ${groupLabel(t.groupCode)} / ${processLabel(t.processType)} 已删除`,
            );
            if (!pending) onChanged();
        } catch (e) {
            setMsg(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
        } finally {
            setBusy('');
        }
    };

    return (
        <div className="card p-4 space-y-3">
            <div className="text-sm font-semibold flex items-center gap-2">
                参数模板
                <span className="text-xs font-normal text-slate-400">
                    设备 <span className="font-mono">{deviceKey || '?'}</span>{' '}
                    在本框架已有 {mine.length} 张模板
                </span>
            </div>
            <div className="text-[11px] text-slate-400">
                参数行的字段按模板来。模板按设备建: 新设备先从相近设备的模板复制一张,
                改好字段保存成本设备的模板,
                再在下面的参数行里选这张模板填数值。模板按 (设备 / 模式组 /
                加工类型) 匹配, 也会被参数行自动命中。
                <b> ⚠ 这里的保存 / 删除立即写入模板库</b>, 不随向导提交一起回滚;
                源模板不会被改动。
            </div>

            {canCreate ? (
                <div className="space-y-2">
                    <div className="text-xs font-semibold text-slate-600">
                        从已有模板复制一张
                    </div>
                    <div className="flex flex-wrap items-end gap-2">
                        <div className="w-[30rem]">
                            <label className="field-label">
                                源模板 (搜设备 / 模式组 / 加工类型) ——
                                复制它的字段, 键沿用它的模式组与加工类型
                            </label>
                            <SearchSelect
                                options={selectOptions}
                                value={picked}
                                onChange={setPicked}
                                placeholder="搜索, 如 F2 Ultra / 浮雕 / 矢量切割"
                                className="w-full"
                                clearable
                            />
                        </div>
                        <button
                            type="button"
                            className="btn-primary"
                            disabled={!picked || !!busy}
                            onClick={openCopyEditor}
                        >
                            复制并编辑字段
                        </button>
                    </div>
                    {pickedTpl &&
                        mine.some(
                            (m) =>
                                m.groupCode === pickedTpl.groupCode &&
                                m.processType === pickedTpl.processType,
                        ) && (
                            <div className="text-[11px] text-amber-600">
                                本设备已有 {groupLabel(pickedTpl.groupCode)} /{' '}
                                {processLabel(pickedTpl.processType)} 模板,
                                保存会覆盖它的字段。
                            </div>
                        )}
                    {msg && <div className="text-xs text-slate-600">{msg}</div>}
                </div>
            ) : (
                <div className="text-xs text-amber-600">
                    你没有参数模板创建权限 (param_schema:create), 无法在这里复制
                    / 编辑模板;
                    可在参数行用「字段来源模板」借用其它设备的模板填值,
                    或联系管理员建模板。
                </div>
            )}

            {mine.length > 0 && (
                <div className="space-y-1 border-t border-slate-100 pt-3">
                    <div className="text-xs font-semibold text-slate-600">
                        本设备已有模板
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                        {mine.map((t) => (
                            <div
                                key={t.key}
                                className="flex items-center gap-2 text-xs border border-slate-100 rounded px-2 py-1.5"
                            >
                                <span className="truncate" title={t.label}>
                                    {groupLabel(t.groupCode)} /{' '}
                                    {processLabel(t.processType)}
                                </span>
                                <span className="text-slate-400 shrink-0">
                                    {t.fields.length} 字段
                                </span>
                                <span className="ml-auto flex items-center gap-2 shrink-0">
                                    {canCreate && (
                                        <button
                                            type="button"
                                            className="text-brand-600 hover:underline"
                                            onClick={() =>
                                                setEditor({
                                                    fields: t.fields,
                                                    layout: t.layout,
                                                    groupCode: t.groupCode,
                                                    processType: t.processType,
                                                    title: '编辑本设备模板',
                                                })
                                            }
                                        >
                                            编辑字段
                                        </button>
                                    )}
                                    {canDelete && (
                                        <button
                                            type="button"
                                            className="btn-danger-text"
                                            disabled={!!busy}
                                            onClick={() => remove(t)}
                                        >
                                            {busy === `del:${t.key}`
                                                ? '删除中…'
                                                : '删除'}
                                        </button>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {editor && (
                <SchemaEditModal
                    open
                    scope="template"
                    dictScope={archCode}
                    initialFields={editor.fields}
                    initialLayout={editor.layout}
                    subtitle={`${editor.title} → 保存为 ${deviceKey} / ${groupLabel(editor.groupCode)} / ${processLabel(editor.processType)}`}
                    hint={
                        <>
                            点「确认」<b>立即写入模板库</b>,
                            与本向导的提交相互独立 —— 之后就算放弃建设备,
                            这张模板也会留在库里
                            (可在「本设备已有模板」里删除)。 源模板不受影响:
                            这里存的是设备{' '}
                            <span className="font-mono">{deviceKey}</span>{' '}
                            名下的新模板。
                        </>
                    }
                    onClose={() => setEditor(null)}
                    onConfirm={async (fields, layout) => {
                        await save(
                            fields,
                            layout,
                            editor.groupCode,
                            editor.processType,
                        );
                        setEditor(null);
                    }}
                />
            )}
        </div>
    );
}

/** SpecInput 单个模板字段的输入控件 (num/int/bool/enum/str), 值按类型写入 (数字存 number). */
function SpecInput({
    f,
    value,
    onChange,
    dictOpts,
}: {
    f: FieldSpec;
    value: unknown;
    onChange: (v: unknown) => void;
    dictOpts: DictOptions;
}) {
    const cur = value === undefined || value === null ? '' : String(value);
    if (f.t === 'enum') {
        const opts = f.os
            ? dictOpts[f.os] || []
            : (f.o || []).map((o) => ({ code: String(o), label: String(o) }));
        return (
            <SearchSelect
                className="w-full text-xs"
                value={cur}
                onChange={(v) => onChange(v)}
                placeholder="未设置"
                options={withCurrent(
                    opts.map((o) => ({ value: o.code, label: o.label })),
                    cur,
                    `${cur} (不在字典)`,
                )}
                clearable
            />
        );
    }
    if (f.t === 'bool') {
        return (
            <SearchSelect
                className="w-full text-xs"
                value={cur === '' ? '' : value ? 'true' : 'false'}
                onChange={(v) => onChange(v === '' ? '' : v === 'true')}
                placeholder="未设置"
                options={BOOL_OPTS}
                clearable
            />
        );
    }
    if (f.t === 'num' || f.t === 'int') {
        return (
            <div className="relative">
                <input
                    className={`inp w-full text-xs font-mono ${f.u ? 'pr-10' : ''}`}
                    type="number"
                    step={f.t === 'int' ? 1 : 'any'}
                    min={f.min}
                    max={f.max}
                    value={cur}
                    onChange={(e) =>
                        onChange(
                            e.target.value === '' ? '' : Number(e.target.value),
                        )
                    }
                />
                {f.u && (
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">
                        {f.u}
                    </span>
                )}
            </div>
        );
    }
    return (
        <input
            className="inp w-full text-xs"
            value={cur}
            onChange={(e) => onChange(e.target.value)}
        />
    );
}

function ParamRowCard({
    index,
    row,
    keyKind,
    processTypes,
    modeOptions,
    tpl,
    tplOptions,
    tplByKey,
    tplLabelOf,
    deviceKey,
    dictOpts,
    fieldNames,
    issueByPath,
    onPatch,
    onParam,
    onDup,
    onRemove,
}: {
    index: number;
    row: BundleDeviceParamItem;
    keyKind: MaterialKeyKind;
    processTypes: { code: string; label: string }[];
    modeOptions: MMMode[];
    tpl:
        | {
              fields: FieldSpec[];
              layout: Layout;
              hit: Required<TplKeyParts> | null;
          }
        | undefined;
    tplOptions: TplOption[];
    tplByKey: Map<string, TplOption>;
    tplLabelOf: (t: TplKeyParts) => string;
    /** 本设备 deviceKey: 候选里标出本设备的模板, 提示"没有本设备模板"时点名 */
    deviceKey: string;
    dictOpts: DictOptions;
    fieldNames: Map<string, string>;
    issueByPath: IssueByPath;
    onPatch: (p: Partial<BundleDeviceParamItem>) => void;
    onParam: (k: string, v: unknown) => void;
    onDup: () => void;
    onRemove: () => void;
}) {
    const [extraKey, setExtraKey] = useState('');
    const params = row.params || {};
    const mode = String(params.processingMode || '');
    // 模板字段 + 模板外已有值的字段 (追加末尾, 标"模板外")
    // 行级"字段来源"覆盖: 新机型没有专属模板时, 直接借别的机型的模板字段来填 (只影响表单渲染,
    // 不进载荷 —— toRequest 只发白名单键). 未覆盖时用自动解析结果.
    const override = row.fieldsFrom ? tplByKey.get(row.fieldsFrom) : undefined;
    const tplFields = (override ? override.fields : tpl?.fields || []).filter(
        (f) => !KEY_FIELD_KEYS.has(f.k),
    );
    // 模板有分组时按 页签 → 分组 展示 (与参数页 / 模板编辑器一致), 否则平铺
    const tplLayout = override ? override.layout : tpl?.layout || null;
    const renderSpec = (f: FieldSpec) => (
        <div>
            <label
                className="field-label flex items-baseline gap-1 whitespace-nowrap overflow-hidden"
                title={f.k}
            >
                {f.required && <span className="text-rose-500">*</span>}
                <span className="shrink-0">
                    {f.label || fieldDisplayName(f.k, fieldNames)}
                </span>
                <span className="text-slate-300 font-mono truncate min-w-0">
                    {f.k}
                </span>
            </label>
            <SpecInput
                f={f}
                value={params[f.k]}
                onChange={(v) => onParam(f.k, v)}
                dictOpts={dictOpts}
            />
        </div>
    );
    // 命中说明: 本设备模板 / 存量机型模板 / 通配. 新设备多半还没有自己的模板 —— 字段不随模式/加工类型变,
    // 是因为该设备还没建模板, 不是选择没生效.
    const hit = tpl?.hit;
    const hitLabel = !hit
        ? ''
        : hit.deviceKey || hit.family || hit.groupCode || hit.processType
          ? tplLabelOf(hit)
          : '框架默认模板';
    const hitIsGeneric = !override && !!hit && !hit.deviceKey && !hit.family;
    // 候选: 本设备模板排最前 (标"本设备"), 其它设备的在后
    const tplSelectOptions = useMemo(() => {
        const own = (t: TplOption) => !!deviceKey && t.deviceKey === deviceKey;
        return [...tplOptions]
            .sort((a, b) => Number(own(b)) - Number(own(a)))
            .map((t) => ({
                value: t.key,
                label: `${own(t) ? '[本设备] ' : ''}${t.label}`,
            }));
    }, [tplOptions, deviceKey]);
    // 这一行的字段怎么来: 选一张已有模板 (加工类型 / 模式由模板定), 还是自己定 (自定义字段).
    // 二选一 —— 两条路都摆出来的话, 选了模板又能改加工类型, 改完模板就不匹配了.
    const fieldMode: 'template' | 'custom' =
        row.fieldMode || (tplOptions.length > 0 ? 'template' : 'custom');
    const setFieldMode = (m: 'template' | 'custom') =>
        onPatch(
            m === 'custom'
                ? { fieldMode: m, fieldsFrom: undefined }
                : { fieldMode: m },
        );
    // 模板已指定的段在表单里只读展示 (通配段仍要自己选)
    const lockedProcess = fieldMode === 'template' && !!override?.processType;
    const lockedMode = fieldMode === 'template' && !!override?.groupCode;

    // 选模板 = 同时把关键字段对齐: 加工类型取模板的; 加工模式换成该模式组下已声明的第一个
    const pickTemplate = (key: string) => {
        const t = key ? tplByKey.get(key) : undefined;
        const patch: Partial<BundleDeviceParamItem> = {
            fieldsFrom: key || undefined,
        };
        if (t) {
            if (t.processType) patch.subProcess = t.processType;
            if (t.groupCode) {
                const curGroup = modeOptions.find(
                    (m) => m.value === mode,
                )?.groupCode;
                if (curGroup !== t.groupCode) {
                    const m = modeOptions.find(
                        (x) => x.groupCode === t.groupCode,
                    );
                    if (m)
                        patch.params = { ...params, processingMode: m.value };
                }
            }
        }
        onPatch(patch);
    };
    const inTpl = new Set(tplFields.map((f) => f.k));
    const extras = Object.keys(params).filter(
        (k) =>
            !inTpl.has(k) && !KEY_FIELD_KEYS.has(k) && params[k] !== undefined,
    );
    const path = `params[${index}]`;
    const processLabelOf = (code?: string) =>
        (code && processTypes.find((p) => p.code === code)?.label) ||
        code ||
        '—';
    // 可搜索下拉的候选 (加工类型 / 加工模式 都有几十个值, 原生 select 翻找慢)
    const processTypeOptions = useMemo(
        () =>
            processTypes.map((p) => ({
                value: p.code,
                label: `${p.label} (${p.code})`,
            })),
        [processTypes],
    );
    const modeSelectOptions = useMemo(
        () =>
            modeOptions.map((m) => ({
                value: m.value,
                label: `${mmModeLabel(m)} (${m.value})`,
            })),
        [modeOptions],
    );
    // 模板外补字段: 只能从 Schema 字段字典挑 (SSOT), 可按 key / 中文名 / 分组搜
    const fieldDict = useSchemaFieldDict();
    // inkjet: 类别 (一级, 二级) 成对选择 / 显示
    const catPair = useCategoryPairOptions(keyKind === 'category_code');
    const categoryValue = encodeCategoryPair(row.type1, row.type2);
    const materialLabel =
        keyKind === 'category_code'
            ? catPair.text(row.type1, row.type2)
            : keyKind === 'dual_xcode'
              ? [row.filmXcode, row.substrateXcode].filter(Boolean).join(' + ')
              : row.xcode;

    return (
        <div className="card p-4 space-y-3">
            <div className="text-sm font-semibold flex items-center gap-2">
                <span className="tag bg-slate-100 text-slate-500 font-normal">
                    #{index + 1}
                </span>
                <span className="font-mono text-xs">
                    {materialLabel || '未选材料'}
                </span>
                <span className="text-xs text-slate-400">
                    {processTypes.find((p) => p.code === row.subProcess)
                        ?.label ||
                        row.subProcess ||
                        '未选加工类型'}
                </span>
                <span className="ml-auto flex items-center gap-2 text-xs font-normal">
                    <button
                        type="button"
                        className="text-brand-600 hover:underline"
                        onClick={onDup}
                    >
                        复制此行
                    </button>
                    <button
                        type="button"
                        className="btn-danger-text"
                        onClick={onRemove}
                    >
                        移除
                    </button>
                </span>
            </div>

            {/* 字段来源: 二选一 —— 用已有模板 (键与字段跟模板走) 或自定义 (自己定加工类型与模式) */}
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] text-slate-400 shrink-0">
                    字段来源
                </span>
                <div className="flex rounded border border-slate-200 overflow-hidden text-xs">
                    {(
                        [
                            ['template', '用已有模板'],
                            ['custom', '自定义'],
                        ] as ['template' | 'custom', string][]
                    ).map(([v, label]) => (
                        <button
                            key={v}
                            type="button"
                            className={`px-2.5 py-1 ${
                                fieldMode === v
                                    ? 'bg-brand-50 text-brand-700 font-semibold'
                                    : 'text-slate-500 hover:bg-slate-50'
                            }`}
                            disabled={
                                v === 'template' && tplOptions.length === 0
                            }
                            onClick={() => setFieldMode(v)}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                {fieldMode === 'template' ? (
                    <>
                        <SearchSelect
                            options={tplSelectOptions}
                            value={row.fieldsFrom || ''}
                            onChange={pickTemplate}
                            placeholder={
                                tplOptions.length
                                    ? '选一张模板, 加工类型与模式跟它走'
                                    : '本框架还没有可用模板'
                            }
                            className="w-[26rem]"
                            clearable
                        />
                        {override && (
                            <span className="text-[11px] text-brand-600">
                                {override.fields.length} 个字段
                            </span>
                        )}
                    </>
                ) : (
                    <span className="text-[11px] text-slate-400">
                        自己选下面的加工类型与加工模式; 字段按自动解析,
                        缺的用「补字段」加
                    </span>
                )}
            </div>

            {/* 关键字段: 决定命中哪张模板 */}
            <div className="grid grid-cols-4 gap-3">
                {keyKind === 'xcode' && (
                    <div>
                        <label className="field-label">
                            <span className="text-rose-500">* </span>材料
                            (xcode)
                        </label>
                        <SearchSelect
                            loadOptions={loadMaterialOptions}
                            value={row.xcode || ''}
                            valueLabel={row.xcode || ''}
                            onChange={(v) => onPatch({ xcode: v })}
                            placeholder="搜索材料"
                            className="w-full"
                            clearable
                        />
                        <FieldIssue issue={issueByPath[`${path}.xcode`]} />
                    </div>
                )}
                {keyKind === 'dual_xcode' && (
                    <>
                        <div>
                            <label className="field-label">
                                <span className="text-rose-500">* </span>膜材
                                (filmXcode)
                            </label>
                            <SearchSelect
                                loadOptions={loadMaterialOptions}
                                value={row.filmXcode || ''}
                                valueLabel={row.filmXcode || ''}
                                onChange={(v) => onPatch({ filmXcode: v })}
                                placeholder="搜索材料"
                                className="w-full"
                                clearable
                            />
                            <FieldIssue
                                issue={issueByPath[`${path}.filmXcode`]}
                            />
                        </div>
                        <div>
                            <label className="field-label">
                                基材 (substrateXcode)
                            </label>
                            <SearchSelect
                                loadOptions={loadMaterialOptions}
                                value={row.substrateXcode || ''}
                                valueLabel={row.substrateXcode || ''}
                                onChange={(v) => onPatch({ substrateXcode: v })}
                                placeholder="单材料加工留空"
                                className="w-full"
                                clearable
                            />
                            <FieldIssue
                                issue={issueByPath[`${path}.substrateXcode`]}
                            />
                        </div>
                    </>
                )}
                {keyKind === 'category_code' && (
                    <div>
                        <label className="field-label">
                            <span className="text-rose-500">* </span>
                            材料分类 (一级 / 二级)
                        </label>
                        <SearchSelect
                            className="w-full"
                            value={categoryValue}
                            onChange={(v) => onPatch(decodeCategoryPair(v))}
                            placeholder="请选择"
                            options={withCurrent(
                                catPair.options,
                                categoryValue,
                                categoryPairFallbackLabel(categoryValue),
                            )}
                        />
                        <FieldIssue
                            issue={issueByPath[`${path}.type2`]}
                        />
                        <FieldIssue
                            issue={issueByPath[`${path}.type1`]}
                        />
                    </div>
                )}
                <div>
                    <label className="field-label">
                        <span className="text-rose-500">* </span>加工类型
                        (subProcess)
                    </label>
                    {lockedProcess ? (
                        <div
                            className="inp w-full bg-slate-50 text-slate-500 flex items-center"
                            title="由所选模板决定"
                        >
                            <span className="truncate">
                                {processLabelOf(row.subProcess)}
                            </span>
                        </div>
                    ) : (
                        <SearchSelect
                            options={processTypeOptions}
                            value={row.subProcess || ''}
                            valueLabel={
                                row.subProcess &&
                                !processTypes.some(
                                    (p) => p.code === row.subProcess,
                                )
                                    ? `${row.subProcess} (不在字典)`
                                    : undefined
                            }
                            onChange={(v) => onPatch({ subProcess: v })}
                            placeholder="搜索加工类型"
                            className="w-full"
                            clearable
                        />
                    )}
                    <FieldIssue issue={issueByPath[`${path}.subProcess`]} />
                </div>
                <div>
                    <label className="field-label">
                        加工模式 (processingMode)
                    </label>
                    {lockedMode ? (
                        <div
                            className="inp w-full bg-slate-50 text-slate-500 flex items-center"
                            title="由所选模板的模式组决定"
                        >
                            <span className="truncate">
                                {modeOptions.find((m) => m.value === mode)
                                    ? mmModeLabel(
                                          modeOptions.find(
                                              (m) => m.value === mode,
                                          )!,
                                      )
                                    : mode || '—'}
                            </span>
                        </div>
                    ) : (
                        <SearchSelect
                            options={modeSelectOptions}
                            value={mode}
                            valueLabel={
                                mode &&
                                !modeOptions.some((m) => m.value === mode)
                                    ? `${mode} (未在第 ② 步声明)`
                                    : undefined
                            }
                            onChange={(v) => onParam('processingMode', v)}
                            placeholder="搜索加工模式"
                            className="w-full"
                            clearable
                        />
                    )}
                </div>
                <div>
                    <label className="field-label">
                        <span className="text-rose-500">* </span>结果标
                    </label>
                    <SearchSelect
                        className="w-full"
                        value={row.resultTag || ''}
                        onChange={(v) => onPatch({ resultTag: v })}
                        placeholder="请选择"
                        options={RESULT_TAG_OPTS}
                    />
                    <FieldIssue issue={issueByPath[`${path}.resultTag`]} />
                </div>
            </div>

            {/* 参数值区: 按模板字段渲染 */}
            <div className="border-t border-slate-100 pt-3 space-y-2">
                <div className="text-xs text-slate-500 flex items-center gap-2">
                    参数值
                    {override ? (
                        <span className="text-brand-600">
                            字段来源: 借用模板{' '}
                            <span className="font-mono">{override.label}</span>
                        </span>
                    ) : tpl === undefined ? (
                        <span className="text-slate-400">模板解析中…</span>
                    ) : tplFields.length === 0 ? (
                        <span className="text-amber-600">
                            没有命中带字段的参数模板 (设备 / 模式 / 加工类型),
                            请手动补字段或先去参数模板页建模板
                        </span>
                    ) : (
                        <span className="text-slate-400">
                            命中模板{' '}
                            <span className="font-mono">{hitLabel}</span> ·{' '}
                            {tplFields.length} 个字段
                        </span>
                    )}
                </div>
                {fieldMode === 'custom' &&
                    hitIsGeneric &&
                    tplFields.length > 0 && (
                        <div className="text-[11px] text-amber-600">
                            设备 <span className="font-mono">{deviceKey}</span>{' '}
                            在本框架还没有自己的参数模板, 所以换加工模式 /
                            加工类型字段也不会变。
                            可在上方「参数模板」卡把相近设备的模板复制成本设备的,
                            或把本行切到「用已有模板」。
                        </div>
                    )}
                {tplLayout && tplFields.length > 0 && (
                    <GroupedFields
                        items={tplFields}
                        attrs={specAttrs}
                        layout={tplLayout}
                        itemKey={specKey}
                        renderItem={renderSpec}
                        gridClassName="grid grid-cols-4 gap-3"
                        collapsible
                    />
                )}
                {(tplFields.length > 0 || extras.length > 0) && (
                    <div className="grid grid-cols-4 gap-3">
                        {!tplLayout &&
                            tplFields.map((f) => (
                                <React.Fragment key={f.k}>
                                    {renderSpec(f)}
                                </React.Fragment>
                            ))}
                        {extras.map((k) => (
                            <div key={k}>
                                <label
                                    className="field-label flex items-baseline gap-1 whitespace-nowrap overflow-hidden"
                                    title={k}
                                >
                                    <span className="shrink-0">
                                        {fieldDisplayName(k, fieldNames)}
                                    </span>
                                    <span className="text-slate-300 font-mono truncate min-w-0">
                                        {k}
                                    </span>
                                    <span className="tag bg-amber-50 text-amber-700 font-normal shrink-0">
                                        模板外
                                    </span>
                                </label>
                                <input
                                    className="inp w-full text-xs font-mono"
                                    value={String(params[k] ?? '')}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        const n = Number(raw);
                                        onParam(
                                            k,
                                            raw !== '' && Number.isFinite(n)
                                                ? n
                                                : raw,
                                        );
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex items-center gap-2">
                    <SchemaFieldKeySelect
                        value={extraKey}
                        byKey={fieldDict.byKey}
                        groups={fieldDict.groups}
                        className="w-72"
                        title="补一个模板外字段 (从 Schema 字段字典搜)"
                        onSelect={(_f, key) => setExtraKey(key)}
                    />
                    <button
                        type="button"
                        className="btn-ghost text-xs"
                        disabled={!extraKey.trim()}
                        onClick={() => {
                            const k = extraKey.trim();
                            const d = fieldDict.byKey.get(k);
                            // 初值按字典类型给: 布尔 false, 其余空串 (进表单再填)
                            onParam(
                                k,
                                params[k] ??
                                    (d?.fieldType === 'boolean' ? false : ''),
                            );
                            setExtraKey('');
                        }}
                    >
                        + 添加字段
                    </button>
                    <span className="text-[11px] text-slate-400">
                        只能选 Schema 字段字典内的 key (可搜 key / 中文名 /
                        分组)
                    </span>
                </div>
                <input
                    className="inp w-full text-xs"
                    placeholder="备注 (可选)"
                    value={row.remark || ''}
                    onChange={(e) => onPatch({ remark: e.target.value })}
                />
                <FieldIssue issue={issueByPath[`${path}.params`]} />
                <FieldIssue issue={issueByPath[path]} />
            </div>
        </div>
    );
}

/** CopyFromDevicePanel 从同框架已有设备复制已发布参数行 (值复制成本设备的新行, deviceKey 由套件注入). */
function CopyFromDevicePanel({
    archCode,
    excludeDeviceKey,
    templateKeyFor,
    keyKind,
    onPick,
    processTypes,
}: {
    archCode: string;
    excludeDeviceKey: string;
    /** 源行实际命中的模板键 (TplOption.key): 复制的行沿用它, 提交时给新设备复制这张模板 (模板按设备建, 新设备没有自己的) */
    templateKeyFor: (
        srcDeviceKey: string,
        srcFamily: string,
        processingMode: string,
        subProcess: string,
    ) => string | undefined;
    keyKind: MaterialKeyKind;
    onPick: (items: BundleDeviceParamItem[]) => void;
    processTypes: { code: string; label: string }[];
}) {
    const [open, setOpen] = useState(false);
    const [deviceKey, setDeviceKey] = useState('');
    // 加工类型筛选 (可选): 源设备一台动辄几千行, 按加工类型缩一下范围
    const [subProc, setSubProc] = useState('');
    const processTypeOptions = useMemo(
        () =>
            processTypes.map((p) => ({
                value: p.code,
                label: `${p.label} (${p.code})`,
            })),
        [processTypes],
    );
    // 同框架已有设备 (源设备候选); 打开面板才拉, 一次拉齐
    const [devices, setDevices] = useState<
        { value: string; label: string; family: string }[]
    >([]);
    useEffect(() => {
        if (!open || devices.length) return;
        listAllDevices({ archCode })
            .then((list) =>
                setDevices(
                    list
                        .filter((d) => d.deviceKey !== excludeDeviceKey)
                        .map((d) => ({
                            value: d.deviceKey,
                            label: d.name
                                ? `${d.deviceKey} · ${d.name}`
                                : d.deviceKey,
                            family: d.family,
                        })),
                ),
            )
            .catch(() => setDevices([]));
    }, [open, archCode, excludeDeviceKey, devices.length]);
    const [rows, setRows] = useState<ParamRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    const [picked, setPicked] = useState<Set<number>>(new Set());

    const search = async () => {
        if (!deviceKey.trim()) {
            setErr('先选一个源设备');
            return;
        }
        setLoading(true);
        setErr('');
        try {
            // 逐页拉齐 (源设备的行可能上千, 上限 COPY_MAX 行防拖垮页面)
            const all: ParamRow[] = [];
            let page = 1;
            let totalCount = 0;
            do {
                const res = await listParams(archCode, {
                    deviceKey: deviceKey.trim(),
                    subProcess: subProc || undefined,
                    status: 'published',
                    page,
                    pageSize: 100,
                    lite: true,
                });
                all.push(...(res.list || []));
                totalCount = res.totalCount || 0;
                page++;
            } while (all.length < totalCount && all.length < COPY_MAX);
            setRows(all.filter((r) => r.deviceKey !== excludeDeviceKey));
            setTotal(totalCount);
            setPicked(new Set());
        } catch (e) {
            setErr(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
        } finally {
            setLoading(false);
        }
    };

    const toggle = (id: number) => {
        const next = new Set(picked);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setPicked(next);
    };

    const copy = () => {
        const items: BundleDeviceParamItem[] = rows
            .filter((r) => picked.has(r.id))
            .map((r) => {
                const params = parseParams(r.params);
                const item: BundleDeviceParamItem = {
                    subProcess: r.subProcess || '',
                    params,
                    resultTag: r.resultTag,
                    remark: r.remark || '',
                };
                const srcFamily =
                    devices.find((d) => d.value === r.deviceKey)?.family || '';
                const tplKey = templateKeyFor(
                    r.deviceKey,
                    srcFamily,
                    String(params.processingMode || ''),
                    r.subProcess || '',
                );
                if (tplKey) {
                    item.fieldMode = 'template';
                    item.fieldsFrom = tplKey;
                }
                if (keyKind === 'category_code') {
                    item.type2 = r.type2 || '';
                    item.type1 = r.type1 || '';
                }
                else if (keyKind === 'dual_xcode') {
                    item.filmXcode = r.filmXcode || '';
                    if (r.substrateXcode)
                        item.substrateXcode = r.substrateXcode;
                } else item.xcode = r.xcode || '';
                return item;
            });
        onPick(items);
        setPicked(new Set());
    };

    const catPair = useCategoryPairOptions(keyKind === 'category_code');
    const materialOf = (r: ParamRow) =>
        keyKind === 'category_code'
            ? catPair.text(r.type1, r.type2)
            : keyKind === 'dual_xcode'
              ? [r.filmXcode, r.substrateXcode].filter(Boolean).join(' + ')
              : r.xcode;

    return (
        <div className="card p-4 space-y-3">
            <button
                type="button"
                className="text-sm font-semibold flex items-center gap-2 w-full text-left"
                onClick={() => setOpen(!open)}
            >
                从已有设备复制参数
                <span className="text-xs font-normal text-slate-400">
                    同框架 {archCode} 的已发布行, 值照搬后可改
                </span>
                <span className="ml-auto text-xs text-slate-400">
                    {open ? '▾' : '▸'}
                </span>
            </button>
            {open && (
                <>
                    <div className="flex flex-wrap items-end gap-2">
                        <div className="w-64">
                            <label className="field-label">源设备</label>
                            <SearchSelect
                                options={devices}
                                value={deviceKey}
                                onChange={(v) => setDeviceKey(v)}
                                placeholder={
                                    devices.length
                                        ? '选择同框架设备'
                                        : '加载设备中…'
                                }
                                className="w-full"
                                clearable
                            />
                        </div>
                        <div className="w-64">
                            <label className="field-label">
                                加工类型 (可选)
                            </label>
                            <SearchSelect
                                options={processTypeOptions}
                                value={subProc}
                                onChange={setSubProc}
                                placeholder="全部加工类型"
                                className="w-full"
                                clearable
                            />
                        </div>
                        <button
                            type="button"
                            className="btn-ghost"
                            onClick={search}
                            disabled={loading}
                        >
                            {loading ? '搜索中…' : '搜索'}
                        </button>
                        {rows.length > 0 && (
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={copy}
                                disabled={picked.size === 0}
                            >
                                复制选中 {picked.size} 行
                            </button>
                        )}
                    </div>
                    {err && <div className="text-xs text-rose-600">{err}</div>}
                    {rows.length > 0 && (
                        <div className="max-h-72 overflow-auto border border-slate-100 rounded">
                            <table className="w-full">
                                <thead>
                                    <tr>
                                        <th className="th w-8">
                                            <input
                                                type="checkbox"
                                                checked={
                                                    picked.size === rows.length
                                                }
                                                onChange={(e) =>
                                                    setPicked(
                                                        e.target.checked
                                                            ? new Set(
                                                                  rows.map(
                                                                      (r) =>
                                                                          r.id,
                                                                  ),
                                                              )
                                                            : new Set(),
                                                    )
                                                }
                                            />
                                        </th>
                                        <th className="th">源设备</th>
                                        <th className="th">材料</th>
                                        <th className="th">加工类型</th>
                                        <th className="th">模式</th>
                                        <th className="th">结果标</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <tr
                                            key={r.id}
                                            className="cursor-pointer hover:bg-slate-50"
                                            onClick={() => toggle(r.id)}
                                        >
                                            <td className="td">
                                                <input
                                                    type="checkbox"
                                                    checked={picked.has(r.id)}
                                                    onChange={() =>
                                                        toggle(r.id)
                                                    }
                                                />
                                            </td>
                                            <td className="td text-xs font-mono">
                                                {r.deviceKey}
                                            </td>
                                            <td className="td text-xs font-mono">
                                                {materialOf(r) || '—'}
                                            </td>
                                            <td className="td text-xs">
                                                {r.subProcess || '—'}
                                            </td>
                                            <td className="td text-xs font-mono">
                                                {String(
                                                    parseParams(r.params)
                                                        .processingMode || '—',
                                                )}
                                            </td>
                                            <td className="td text-xs">
                                                {RESULT_TAGS.find(
                                                    (t) => t.v === r.resultTag,
                                                )?.label || r.resultTag}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {total > rows.length && (
                                <div className="text-[11px] text-slate-400 px-2 py-1">
                                    共 {total} 行, 只显示前 {rows.length} 行;
                                    用关键词缩小范围
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default StepDeviceParams;
